'use client'

import { useEffect, useState } from 'react'
import styles from './Scroller.module.css'
import IconButton, {
  IconButtonKind,
  IconButtonSize,
} from '@/components/UI/Buttons/IconButton/IconButton'
import IconDownArrow from '@/assets/img/Icon_ArrowDown.svg'
import IconUpArrow from '@/assets/img/Icon_ArrowUp.svg'

interface ScrollerProps {
  content: string
}

export default function Scroller({ content }: ScrollerProps) {
  const [currentSection, setCurrentSection] = useState(0)
  const [sections, setSections] = useState<HTMLElement[]>([])

  // Resolve sections from DOM (re-run once to cover async/hydrated content)
  useEffect(() => {
    const updateSections = () => {
      const elements = Array.from(
        window.document.querySelectorAll('[data-scroll-section]')
      ) as HTMLElement[]

      setSections(elements)
    }

    updateSections()

    const timeout = window.setTimeout(updateSections, 300)
    return () => window.clearTimeout(timeout)
  }, [])

  // Determine active section from visibility.
  // If the last section is close enough to the viewport, force it as active.
  useEffect(() => {
    if (!sections.length) return

    const visible = new Map<HTMLElement, number>()

    const observer = new IntersectionObserver(
      (entries) => {
        const lastSection = sections[sections.length - 1]
        const lastRect = lastSection.getBoundingClientRect()

        // If the last section has entered the viewport enough, treat it as active.
        if (lastRect.top <= window.innerHeight * 0.8) {
          setCurrentSection(sections.length - 1)
          return
        }

        entries.forEach((entry) => {
          const el = entry.target as HTMLElement

          if (entry.isIntersecting) {
            visible.set(el, entry.intersectionRatio)
          } else {
            visible.delete(el)
          }
        })

        if (visible.size) {
          const [mostVisible] = [...visible.entries()].sort(
            (a, b) => b[1] - a[1]
          )[0]

          const index = sections.indexOf(mostVisible)
          if (index !== -1) {
            setCurrentSection(index)
          }

          return
        }

        // Fallback: closest to viewport center
        const mid = window.innerHeight / 2

        let closest = 0
        let minDist = Number.POSITIVE_INFINITY

        sections.forEach((section, i) => {
          const rect = section.getBoundingClientRect()
          const center = rect.top + rect.height / 2
          const dist = Math.abs(center - mid)

          if (dist < minDist) {
            minDist = dist
            closest = i
          }
        })

        setCurrentSection(closest)
      },
      {
        threshold: [0, 0.15, 0.3, 0.5, 0.75],
      }
    )

    sections.forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [sections])

  const isLast = sections.length > 0 && currentSection === sections.length - 1

  // Scroll forward or loop back to start
  const handleClick = () => {
    if (!sections.length) return

    const nextIndex = isLast ? 0 : currentSection + 1

    sections[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <div className={styles.scrollerWrapper}>
      <div className={styles.content}>
        {sections.length > 0 && currentSection === 0 &&
          <p className="bodyTextSmallStrong">{content}</p>
        }

        <IconButton
          type="button"
          icon={isLast ? IconUpArrow : IconDownArrow}
          kind={IconButtonKind.FILLED}
          size={IconButtonSize.L}
          onClick={handleClick}
          title={'Scroll button'}
        />
      </div>
    </div>
  )
}